/**
 * CON-28 — browser RUM noise filters shared by every `@dash0/sdk-web`
 * init (apps/web, apps/admin `instrumentation-client.ts`, apps/auth
 * `rum-client.ts`).
 *
 * The SDK records every rejected `fetch` as a request error. In a
 * Next.js App Router app the router prefetches `<Link>` targets on
 * hover/viewport and aborts them the moment the user navigates, so a
 * normal click produces several `browser.request … Failed to fetch`
 * events per page — 17 "errors" over 13 sessions on 2026-09-07, none
 * user-visible — and the spec 98 C.14 error-rate check fired on them.
 *
 * Two layers, both pure data so they are unit-testable:
 *   - `RUM_IGNORE_URLS` drops the router's RSC fetches (`?_rsc=`) from
 *     request instrumentation entirely. Page views, navigation timing
 *     and Web Vitals are unaffected; the RSC payload fetch is not a
 *     user-facing request.
 *   - `RUM_IGNORE_ERROR_MESSAGES` drops the abort-shaped error
 *     messages browsers throw for cancelled requests. A real thrown
 *     error on a page (TypeError from app code, a 500 on a data call)
 *     still counts.
 *
 * Spec 192-c D11 §1 — a vendor's short-lived download link is a bearer URL
 * for its lifetime. It reaches the browser as a `<video src>` / `<img src>`
 * / download `href`, so RUM must never record it: request instrumentation
 * ignores every URL off connect0's hosts (`RUM_FOREIGN_URL`), and
 * `scrubForeignRumUrl` (the SDK's `urlAttributeScrubber`, applied to every
 * `url.*` and `page.url.*` attribute, resource and navigation entries
 * included) keeps only a foreign URL's domain.
 */

/** Hosts whose URLs RUM may record whole: connect0's own, and local development. */
export const RUM_FIRST_PARTY_HOST = /^(?:(?:[a-z0-9-]+\.)*connect0\.ai|localhost|127\.0\.0\.1)$/i;

/** An absolute http(s) URL whose host is not a connect0 host (spec 192-c D11 §1). */
export const RUM_FOREIGN_URL =
  /^https?:\/\/(?!(?:[a-z0-9-]+\.)*connect0\.ai(?:[:/?#]|$)|localhost(?:[:/?#]|$)|127\.0\.0\.1(?:[:/?#]|$))/i;

/** Request URLs never reported: Next.js RSC / prefetch payload fetches, and any URL off connect0's hosts. */
export const RUM_IGNORE_URLS: readonly RegExp[] = [/[?&]_rsc=/, RUM_FOREIGN_URL];

/** Error messages never reported: the cancelled-request family across browsers. */
export const RUM_IGNORE_ERROR_MESSAGES: readonly RegExp[] = [
  /^(TypeError: )?Failed to fetch$/, // Chromium: aborted or blocked fetch
  /^(TypeError: )?NetworkError when attempting to fetch resource\.?$/, // Firefox
  /^(TypeError: )?Load failed$/, // Safari
  /AbortError|The operation was aborted|signal is aborted|The user aborted a request/,
];

/** The SDK's `url.*` attribute record (OTel semantic-convention keys). */
export interface RumUrlAttributes {
  'url.full': string;
  'url.path'?: string | undefined;
  'url.domain'?: string | undefined;
  'url.scheme'?: string | undefined;
  'url.fragment'?: string | undefined;
  'url.query'?: string | undefined;
}

/**
 * Spec 192-c D11 §1 — a foreign URL keeps only its scheme and domain; its
 * path (a bearer link is a path), query and fragment never leave the browser.
 */
export function scrubForeignRumUrl(attrs: RumUrlAttributes): RumUrlAttributes {
  const domain = attrs['url.domain'] ?? '';
  if (RUM_FIRST_PARTY_HOST.test(domain)) return attrs;
  const scheme = attrs['url.scheme'] ?? 'https';
  return {
    'url.full': `${scheme}://${domain}/REDACTED`,
    'url.path': 'REDACTED',
    'url.domain': domain,
    'url.scheme': scheme,
    'url.fragment': undefined,
    'url.query': undefined,
  };
}

export interface RumNoiseOptions {
  ignoreUrls: RegExp[];
  ignoreErrorMessages: RegExp[];
  urlAttributeScrubber: (attrs: RumUrlAttributes) => RumUrlAttributes;
}

/** Spread into `init({...})` of `@dash0/sdk-web`. Fresh arrays each call — the SDK may mutate its config. */
export function rumNoiseOptions(): RumNoiseOptions {
  return {
    ignoreUrls: [...RUM_IGNORE_URLS],
    ignoreErrorMessages: [...RUM_IGNORE_ERROR_MESSAGES],
    urlAttributeScrubber: scrubForeignRumUrl,
  };
}

/** True when a request URL would be dropped — exported for tests and for the SSR side to mirror the rule. */
export function isIgnoredRumUrl(url: string): boolean {
  return RUM_IGNORE_URLS.some((re) => re.test(url));
}

/** True when an error message would be dropped. */
export function isIgnoredRumErrorMessage(message: string): boolean {
  return RUM_IGNORE_ERROR_MESSAGES.some((re) => re.test(message));
}
